import React, { useState } from 'react';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  XMarkIcon,
  Bars3Icon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import { useFormFields } from '../hooks/useFormFields';
import api, { API_ENDPOINTS } from '../config/api';

const FormFieldManager = ({ isOpen, onClose }) => {
  const { fields, loading, refetch } = useFormFields();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [draggedField, setDraggedField] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [newField, setNewField] = useState({
    name: '',
    label: '',
    type: 'text',
    placeholder: '',
    required: false,
    options: [],
    order: 0,
    insertAfter: ''
  });

  const fieldTypes = [
    { value: 'text', label: 'Text Input' },
    { value: 'email', label: 'Email Input' },
    { value: 'phone', label: 'Phone Input' },
    { value: 'number', label: 'Number Input' },
    { value: 'textarea', label: 'Text Area' },
    { value: 'select', label: 'Dropdown Select' },
    { value: 'date', label: 'Date Picker' }
  ];

  const resetForm = () => {
    setNewField({
      name: '',
      label: '',
      type: 'text',
      placeholder: '',
      required: false,
      options: [],
      order: fields.length,
      insertAfter: ''
    });
  };

  const handleAddField = async () => {
    // Validate required fields
    if (!newField.label || !newField.type) {
      alert('Label and Type are required fields');
      return;
    }

    // Calculate order based on insertAfter selection
    let calculatedOrder = fields.length;
    
    if (newField.insertAfter) {
      const builtInFieldOrder = {
        'name': 1,
        'email': 2,
        'phone': 3,
        'source': 4,
        'budget': 5
      };
      
      if (builtInFieldOrder[newField.insertAfter]) {
        calculatedOrder = builtInFieldOrder[newField.insertAfter] + 0.5;
      } else {
        const afterField = fields.find(f => f.name === newField.insertAfter);
        if (afterField) {
          calculatedOrder = afterField.order + 0.5;
        }
      }
    }

    const fieldData = {
      ...newField,
      name: newField.name || newField.label.toLowerCase().replace(/\s+/g, '_'),
      order: calculatedOrder
    };

    try {
      console.log('🔍 Sending field data:', JSON.stringify(fieldData, null, 2));
      
      const response = await api.post(API_ENDPOINTS.FORM_FIELDS, fieldData);
      console.log('✅ Field created successfully:', response.data);
      
      await refetch();
      setShowAddModal(false);
      resetForm();
    } catch (error) {
      console.error('❌ Error adding field:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      
      // Show user-friendly error message
      if (error.response?.data?.message) {
        if (error.response.data.message.includes('Field name already exists')) {
          const fieldName = fieldData.name;
          const confirmReplace = window.confirm(
            `A field with the name "${fieldName}" already exists. This might be a previously deleted field that still exists in the database.\n\nWould you like to try adding the field with a different name by appending a timestamp?`
          );
          
          if (confirmReplace) {
            // Try again with a timestamp suffix
            const timestamp = Date.now();
            const newFieldData = {
              ...fieldData,
              name: `${fieldData.name}_${timestamp}`,
              label: `${fieldData.label} (${timestamp})`
            };
            
            try {
              console.log('🔄 Retrying with unique name:', JSON.stringify(newFieldData, null, 2));
              const retryResponse = await api.post(API_ENDPOINTS.FORM_FIELDS, newFieldData);
              console.log('✅ Field created successfully with unique name:', retryResponse.data);
              
              await refetch();
              setShowAddModal(false);
              resetForm();
              return; // Exit successfully
            } catch (retryError) {
              console.error('❌ Retry also failed:', retryError);
              alert('Failed to create field even with unique name. Please try a different field name.');
            }
          }
        } else {
          alert(`Error: ${error.response.data.message}`);
        }
      } else {
        alert('Failed to create field. Please check console for details.');
      }
    }
  };

  const handleEditField = async () => {
    try {
      const fieldData = {
        ...editingField,
        name: editingField.name.toLowerCase().replace(/\s+/g, '_')
      };

      await api.put(`${API_ENDPOINTS.FORM_FIELDS}/${editingField._id}`, fieldData);
      await refetch();
      setEditingField(null);
    } catch (error) {
      console.error('Error updating field:', error);
    }
  };

  const handleDeleteField = async (fieldId) => {
    // Find the field to check if it's built-in
    const field = fields.find(f => f._id === fieldId);
    
    // Check if it's a built-in field
    if (field && field.builtin) {
      alert('Built-in fields cannot be deleted from the database. They are part of the core form structure.');
      return;
    }
    
    // Also check for built-in field names (fallback)
    const builtInFields = ['name', 'email', 'phone', 'source', 'budget'];
    if (typeof fieldId === 'string' && builtInFields.includes(fieldId)) {
      alert('Built-in fields cannot be deleted from the database. They are part of the core form structure.');
      return;
    }

    if (window.confirm('Are you sure you want to delete this field?')) {
      try {
        console.log('🗑️ Attempting to delete field with ID:', fieldId);
        console.log('🔗 Delete URL:', `${API_ENDPOINTS.FORM_FIELDS}/${fieldId}`);
        
        const response = await api.delete(`${API_ENDPOINTS.FORM_FIELDS}/${fieldId}`);
        console.log('✅ Delete response:', response);
        
        await refetch();
        console.log('✅ Field deleted successfully and data refreshed');
      } catch (error) {
        console.error('❌ Error deleting field:', error);
        console.error('❌ Error response:', error.response?.data);
        console.error('❌ Error status:', error.response?.status);
        
        // Show more detailed error message
        if (error.response?.status === 404) {
          alert('Field not found. It may have already been deleted.');
        } else if (error.response?.status === 403) {
          alert('You do not have permission to delete this field.');
        } else if (error.response?.data?.message) {
          alert(`Error: ${error.response.data.message}`);
        } else {
          alert('Failed to delete field. Please check console for details.');
        }
      }
    }
  };


  const handleMoveField = async (fieldId, direction) => {
    try {
      const fieldIndex = fields.findIndex(f => f._id === fieldId);
      if (fieldIndex === -1) return;

      const field = fields[fieldIndex];
      let newOrder;

      if (direction === 'up' && fieldIndex > 0) {
        const prevField = fields[fieldIndex - 1];
        newOrder = prevField.order - 0.1;
      } else if (direction === 'down' && fieldIndex < fields.length - 1) {
        const nextField = fields[fieldIndex + 1];
        newOrder = nextField.order + 0.1;
      } else {
        return; // Can't move further
      }

      await api.put(`${API_ENDPOINTS.FORM_FIELDS}/${fieldId}`, {
        ...field,
        order: newOrder
      });

      await refetch();
    } catch (error) {
      console.error('Error moving field:', error);
      alert('Failed to move field');
    }
  };

  const handleReorderField = async (fieldId, direction) => {
    try {
      await api.patch(`${API_ENDPOINTS.FORM_FIELDS}/${fieldId}/reorder`, {
        direction
      });
      await refetch();
    } catch (error) {
      console.error('Error reordering field:', error);
    }
  };

  const handleDragStart = (e, field, index) => {
    setDraggedField({ field, index });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedField(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    if (!draggedField || draggedField.index === dropIndex) {
      setDragOverIndex(null);
      return;
    }

    try {
      const draggedFieldData = draggedField.field;
      
      // Only allow reordering of custom fields
      if (draggedFieldData.builtin) {
        setDragOverIndex(null);
        setDraggedField(null);
        return;
      }

      // Get all fields combined (built-in + custom) sorted by order
      const allFields = [
        { name: 'name', label: 'Full Name', type: 'text', required: true, builtin: true, order: 1 },
        { name: 'email', label: 'Email', type: 'email', required: true, builtin: true, order: 2 },
        { name: 'phone', label: 'Phone', type: 'phone', required: true, builtin: true, order: 3 },
        { name: 'source', label: 'Lead Source', type: 'select', required: true, builtin: true, order: 4 },
        { name: 'budget', label: 'Budget Range', type: 'select', required: false, builtin: true, order: 5 },
        ...fields
      ].sort((a, b) => (a.order || 0) - (b.order || 0));
      
      // Calculate new order based on drop position
      let newOrder;
      if (dropIndex === 0) {
        // Dropped at the beginning
        newOrder = 0.5;
      } else if (dropIndex >= allFields.length - 1) {
        // Dropped at the end
        const lastOrder = allFields[allFields.length - 1]?.order || 0;
        newOrder = lastOrder + 1;
      } else {
        // Dropped in the middle
        const prevOrder = allFields[dropIndex - 1]?.order || 0;
        const nextOrder = allFields[dropIndex]?.order || 0;
        newOrder = (prevOrder + nextOrder) / 2;
      }

      await api.put(`${API_ENDPOINTS.FORM_FIELDS}/${draggedFieldData._id}`, {
        ...draggedFieldData,
        order: newOrder
      });

      await refetch();
    } catch (error) {
      console.error('Error reordering field:', error);
      alert('Failed to reorder field');
    }

    setDragOverIndex(null);
    setDraggedField(null);
  };

  const addOption = (isEditing = false) => {
    const target = isEditing ? editingField : newField;
    const setter = isEditing ? setEditingField : setNewField;
    
    setter({
      ...target,
      options: [...target.options, { value: '', label: '' }]
    });
  };

  const updateOption = (index, key, value, isEditing = false) => {
    const target = isEditing ? editingField : newField;
    const setter = isEditing ? setEditingField : setNewField;
    
    const updatedOptions = [...target.options];
    updatedOptions[index][key] = value;
    
    setter({
      ...target,
      options: updatedOptions
    });
  };

  const removeOption = (index, isEditing = false) => {
    const target = isEditing ? editingField : newField;
    const setter = isEditing ? setEditingField : setNewField;
    
    setter({
      ...target,
      options: target.options.filter((_, i) => i !== index)
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Form Field Manager</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-lg hover:bg-gray-100"
            title="Close Form Field Manager"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Back/Close Button */}
          <div className="mb-4">
            <button
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              ← Back to Dashboard
            </button>
          </div>
          {/* Add Field Button */}
          <div className="mb-6 text-center">
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add New Field to Form
            </button>
          </div>

          {/* Current Form Structure with Drag and Drop */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Form Structure</h3>
            <div className="bg-gray-50 rounded-lg p-6 border">
              <div className="space-y-3">
                {/* All fields combined and sorted by order */}
                {[
                  { name: 'name', label: 'Full Name', type: 'text', required: true, builtin: true, order: 1 },
                  { name: 'email', label: 'Email', type: 'email', required: true, builtin: true, order: 2 },
                  { name: 'phone', label: 'Phone', type: 'phone', required: true, builtin: true, order: 3 },
                  { name: 'source', label: 'Lead Source', type: 'select', required: true, builtin: true, order: 4 },
                  { name: 'budget', label: 'Budget Range', type: 'select', required: false, builtin: true, order: 5 },
                  ...fields
                ]
                  .sort((a, b) => a.order - b.order)
                  .map((field, index) => (
                    <div 
                      key={field._id || field.name}
                      className={`flex items-center p-3 bg-white rounded border hover:bg-gray-50 transition-colors ${
                        dragOverIndex === index ? 'border-blue-500 bg-blue-50' : ''
                      } ${field.builtin ? '' : 'cursor-move'}`}
                      draggable={true}
                      onDragStart={(e) => handleDragStart(e, field, index)}
                      onDragEnd={handleDragEnd}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, index)}
                    >
                      {/* Drag Handle - for all fields */}
                      <div className="mr-3 cursor-move">
                        <Bars3Icon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                      </div>
                      
                      <div className="flex items-center space-x-3 flex-1">
                        <span className="text-sm font-medium text-gray-900">{field.label}</span>
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                          {field.builtin ? 'Built-in' : (fieldTypes.find(t => t.value === field.type)?.label || field.type)}
                        </span>
                        {field.required && (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">Required</span>
                        )}
                        {field.type === 'select' && field.options && (
                          <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">
                            {field.options.length} company options
                          </span>
                        )}
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center space-x-2">
                        {/* Edit Button - for all fields */}
                        <button
                          onClick={() => setEditingField(field)}
                          className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded"
                          title="Edit field"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        
                        {/* Delete Button - only for custom fields */}
                        {!field.builtin && field._id && (
                          <button
                            onClick={() => handleDeleteField(field._id)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded"
                            title="Delete field"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* Custom Fields Management */}
          {fields.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Manage Custom Fields</h3>
              <div className="space-y-4">
                {fields.map((field, index) => (
                  <div key={field._id} className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="font-medium text-gray-900">{field.label}</h3>
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                            {fieldTypes.find(t => t.value === field.type)?.label}
                          </span>
                          {field.required && (
                            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                              Required
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">
                          Field Name: <code className="bg-gray-200 px-1 rounded">{field.name}</code>
                        </p>
                        {field.placeholder && (
                          <p className="text-sm text-gray-600">
                            Placeholder: {field.placeholder}
                          </p>
                        )}
                        {field.options && field.options.length > 0 && (
                          <div className="mt-2">
                            <span className="text-xs text-gray-500">Options: </span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {field.options.map((option, i) => (
                                <span key={i} className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded">
                                  {option.label}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setEditingField(field)}
                          className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteField(field._id)}
                          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Add Field Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-semibold">Add New Field</h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              
              <div className="p-4 overflow-y-auto max-h-[calc(80vh-120px)]">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Field Label *
                      </label>
                      <input
                        type="text"
                        value={newField.label}
                        onChange={(e) => setNewField({...newField, label: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter field label"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Field Type *
                      </label>
                      <select
                        value={newField.type}
                        onChange={(e) => setNewField({...newField, type: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {fieldTypes.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Field Name (Auto-generated)
                    </label>
                    <input
                      type="text"
                      value={newField.label.toLowerCase().replace(/\s+/g, '_')}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Placeholder Text
                    </label>
                    <input
                      type="text"
                      value={newField.placeholder}
                      onChange={(e) => setNewField({...newField, placeholder: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter placeholder text"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Required Field
                    </label>
                    <input
                      type="checkbox"
                      id="required"
                      checked={newField.required}
                      onChange={(e) => setNewField({...newField, required: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="required" className="ml-2 text-sm text-gray-700">
                      Required field
                    </label>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Field Position
                    </label>
                    <select
                      value={newField.insertAfter || ''}
                      onChange={(e) => setNewField({...newField, insertAfter: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Add at the end</option>
                      <option value="name">After Full Name</option>
                      <option value="email">After Email Address</option>
                      <option value="phone">After Phone Number</option>
                      <option value="source">After Lead Source</option>
                      <option value="budget">After Budget Range</option>
                      {fields.map((field) => (
                        <option key={field._id} value={field.name}>
                          After {field.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Choose where to place this field in the form
                    </p>
                  </div>
                  
                  {newField.type === 'select' && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Dropdown Options
                        </label>
                        <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                          Company-specific options
                        </span>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                        <p className="text-sm text-blue-800">
                          <strong>Note:</strong> These options will be specific to your company. Each company can have different dropdown options for the same field.
                        </p>
                      </div>
                      <div className="space-y-2">
                        {newField.options.map((option, index) => (
                          <div key={index} className="flex space-x-2">
                            <input
                              type="text"
                              value={option.value}
                              onChange={(e) => updateOption(index, 'value', e.target.value)}
                              placeholder="Option value (internal)"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <input
                              type="text"
                              value={option.label}
                              onChange={(e) => updateOption(index, 'label', e.target.value)}
                              placeholder="Option display text"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <button
                              onClick={() => removeOption(index)}
                              className="p-2 text-red-600 hover:text-red-800"
                              title="Remove option"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={() => addOption()}
                          className="flex items-center text-blue-600 hover:text-blue-800 text-sm"
                        >
                          <PlusIcon className="h-4 w-4 mr-1" />
                          Add Option
                        </button>
                      </div>
                      
                      {/* Quick option templates */}
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Quick Templates (Optional)
                        </label>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              const priorityOptions = [
                                { value: 'low', label: 'Low Priority' },
                                { value: 'medium', label: 'Medium Priority' },
                                { value: 'high', label: 'High Priority' },
                                { value: 'urgent', label: 'Urgent' }
                              ];
                              setNewField({...newField, options: priorityOptions});
                            }}
                            className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200"
                          >
                            Priority Levels
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const statusOptions = [
                                { value: 'active', label: 'Active' },
                                { value: 'inactive', label: 'Inactive' },
                                { value: 'pending', label: 'Pending' }
                              ];
                              setNewField({...newField, options: statusOptions});
                            }}
                            className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200"
                          >
                            Status Options
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const categoryOptions = [
                                { value: 'category1', label: 'Category 1' },
                                { value: 'category2', label: 'Category 2' },
                                { value: 'category3', label: 'Category 3' }
                              ];
                              setNewField({...newField, options: categoryOptions});
                            }}
                            className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200"
                          >
                            Categories
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 p-4 border-t">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddField}
                  disabled={!newField.label || !newField.type}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Add Field
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Field Modal */}
        {editingField && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-semibold">Edit Field</h3>
                <button
                  onClick={() => setEditingField(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              
              <div className="p-4 overflow-y-auto max-h-[calc(80vh-120px)]">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Field Label *
                      </label>
                      <input
                        type="text"
                        value={editingField.label}
                        onChange={(e) => setEditingField({...editingField, label: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Field Type *
                      </label>
                      <select
                        value={editingField.type}
                        onChange={(e) => setEditingField({...editingField, type: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {fieldTypes.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Placeholder Text
                    </label>
                    <input
                      type="text"
                      value={editingField.placeholder}
                      onChange={(e) => setEditingField({...editingField, placeholder: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="editRequired"
                      checked={editingField.required}
                      onChange={(e) => setEditingField({...editingField, required: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="editRequired" className="ml-2 text-sm text-gray-700">
                      Required field
                    </label>
                  </div>
                  
                  {editingField.type === 'select' && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Dropdown Options
                        </label>
                        <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                          Company-specific options
                        </span>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                        <p className="text-sm text-blue-800">
                          <strong>Note:</strong> These options are specific to your company. Other companies will have their own separate options for this field.
                        </p>
                      </div>
                      <div className="space-y-2">
                        {editingField.options?.map((option, index) => (
                          <div key={index} className="flex space-x-2">
                            <input
                              type="text"
                              value={option.value}
                              onChange={(e) => updateOption(index, 'value', e.target.value, true)}
                              placeholder="Option value (internal)"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <input
                              type="text"
                              value={option.label}
                              onChange={(e) => updateOption(index, 'label', e.target.value, true)}
                              placeholder="Option display text"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <button
                              onClick={() => removeOption(index, true)}
                              className="p-2 text-red-600 hover:text-red-800"
                              title="Remove option"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={() => addOption(true)}
                          className="flex items-center text-blue-600 hover:text-blue-800 text-sm"
                        >
                          <PlusIcon className="h-4 w-4 mr-1" />
                          Add Option
                        </button>
                      </div>
                      
                      {/* Quick option templates for editing */}
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Replace with Template (Optional)
                        </label>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              const priorityOptions = [
                                { value: 'low', label: 'Low Priority' },
                                { value: 'medium', label: 'Medium Priority' },
                                { value: 'high', label: 'High Priority' },
                                { value: 'urgent', label: 'Urgent' }
                              ];
                              setEditingField({...editingField, options: priorityOptions});
                            }}
                            className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200"
                          >
                            Priority Levels
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const statusOptions = [
                                { value: 'active', label: 'Active' },
                                { value: 'inactive', label: 'Inactive' },
                                { value: 'pending', label: 'Pending' }
                              ];
                              setEditingField({...editingField, options: statusOptions});
                            }}
                            className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200"
                          >
                            Status Options
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const categoryOptions = [
                                { value: 'category1', label: 'Category 1' },
                                { value: 'category2', label: 'Category 2' },
                                { value: 'category3', label: 'Category 3' }
                              ];
                              setEditingField({...editingField, options: categoryOptions});
                            }}
                            className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200"
                          >
                            Categories
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 p-4 border-t">
                <button
                  onClick={() => setEditingField(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditField}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Update Field
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FormFieldManager;
